import { ref, type Ref } from 'vue';
import { approvalConfigApi, type PreviousRevisionsModule } from '../api/approval-config.api';
import { useFormBuilderToast } from './useFormBuilderToast';

export interface UsePreviousRevisionsModuleReturn {
  lockedModule: Ref<PreviousRevisionsModule | null>;
  isLoadingLockedModule: Ref<boolean>;
  loadPreviousRevisionsModule: (prefix: string) => Promise<void>;
}

export function usePreviousRevisionsModule(): UsePreviousRevisionsModuleReturn {
  const { showErrorToast } = useFormBuilderToast();
  const lockedModule = ref<PreviousRevisionsModule | null>(null);
  const isLoadingLockedModule = ref(false);
  let requestSequence = 0;

  async function loadPreviousRevisionsModule(prefix: string): Promise<void> {
    // The RQ can change faster than its request answers; only the newest request may publish,
    // otherwise a slow earlier response would lock the module of an RQ no longer on screen.
    const sequence = ++requestSequence;
    const trimmed = prefix.trim();

    if (trimmed === '') {
      lockedModule.value = null;
      isLoadingLockedModule.value = false;
      return;
    }

    isLoadingLockedModule.value = true;
    try {
      const module = await approvalConfigApi.getPreviousRevisionsModule(trimmed);
      if (sequence !== requestSequence) return;
      lockedModule.value = module;
    } catch {
      if (sequence !== requestSequence) return;
      lockedModule.value = null;
      showErrorToast({
        title: 'Erro ao carregar o módulo das revisões anteriores, tente novamente.',
        type: 'error',
        hideIcon: true,
      });
    } finally {
      if (sequence === requestSequence) isLoadingLockedModule.value = false;
    }
  }

  return { lockedModule, isLoadingLockedModule, loadPreviousRevisionsModule };
}
